import { Head, Link, router, useForm } from '@inertiajs/react'
import { AlertTriangle, ArrowLeft, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { getRememberedUrl } from '@/lib/rememberedUrl'
import { companiesPath, companyPath } from '@/lib/routes'

import {
  CompanyFormFields,
  type BatchOption,
  type CompanyFormData,
  type CompanyStatus,
  type CountryOption,
  type IndustryOption,
} from './_Form'

type EditCompany = Omit<CompanyFormData, 'logo' | 'remove_logo'> & {
  id: number
  slug: string
  logo_preview_url: string | null
}

type CompaniesEditProps = {
  company: EditCompany
  batches: BatchOption[]
  countries: CountryOption[]
  industries: IndustryOption[]
}

function CompaniesEdit({ company, batches, countries, industries }: CompaniesEditProps) {
  const form = useForm<CompanyFormData>({
    name: company.name ?? '',
    legal_name: company.legal_name ?? '',
    oneliner_en: company.oneliner_en ?? '',
    oneliner_es: company.oneliner_es ?? '',
    description_en: company.description_en ?? '',
    description_es: company.description_es ?? '',
    webpage: company.webpage ?? '',
    linkedin: company.linkedin ?? '',
    country: company.country ?? '',
    logo: null,
    remove_logo: false,
    industry_ids: company.industry_ids ?? [],
    founded_year: company.founded_year ?? '',
    status: (company.status as CompanyStatus) ?? 'operating',
    batch_id: company.batch_id ?? null,
    female_founder: Boolean(company.female_founder),
  })

  // Return to the filtered/sorted index the user last saw, not the bare path.
  const backToIndex = getRememberedUrl('companies', companiesPath())

  return (
    <>
      <Head title={`${company.name} — Companies`} />

      <div className="mx-auto max-w-3xl px-8 py-10">
        <Link
          href={backToIndex}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to companies
        </Link>

        <PageHeader
          title={company.name}
          description={
            <>
              <code className="rounded bg-muted px-1.5 py-0.5 text-xs">/companies/{company.slug}</code>
            </>
          }
        />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ company: data }))
            form.patch(companyPath(company.slug as string), { forceFormData: true })
          }}
        >
          <CompanyFormFields
            data={form.data}
            errors={form.errors as Partial<Record<keyof CompanyFormData, string>>}
            setData={form.setData}
            batches={batches}
            countries={countries}
            industries={industries}
            existingLogoUrl={company.logo_preview_url ?? null}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Link href={backToIndex}>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>

        <Card className="mt-6 border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">Archive company</CardTitle>
            <CardDescription>
              The company is soft-deleted and hidden from lists. Investments, events and
              valuations remain for historical integrity.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => {
                if (window.confirm(`Archive ${company.name}?`)) {
                  router.delete(companyPath(company.slug))
                }
              }}
            >
              <AlertTriangle />
              <Trash2 />
              Archive
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

export default Object.assign(CompaniesEdit, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
